"use client";

import React from "react";
import { Sparkles, Layers } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";
import {
  EcommerceTermsProvider,
  useEcommerceTerms,
} from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";

declare global {
  interface Window {
    wvcClient?: {
      getCurrentWPTermQueryParams: () => Record<string, unknown>;
    };
  }
}

function DynamicBreadcrumbsConsumer() {
  const { links, loading, error } = useShopBreadcrumbs();

  if (loading || error || !links || links.length === 0) {
    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/HomePage" className="text-muted-foreground hover:text-foreground">
                Inicio
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="font-medium text-foreground">
              Tienda
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  const lastIndex = links.length - 1;

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {links.map((link, index) => (
          <React.Fragment key={link.href || index}>
            <BreadcrumbItem>
              {index === lastIndex ? (
                <BreadcrumbPage className="font-medium text-foreground">
                  {link.title}
                </BreadcrumbPage>
              ) : (
                <BreadcrumbLink asChild>
                  <Link to={link.href} className="text-muted-foreground hover:text-foreground">
                    {link.title}
                  </Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {index < lastIndex && <BreadcrumbSeparator />}
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function ArchiveHeaderContentConsumer() {
  const { terms, loading } = useEcommerceTerms();

  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3.5 py-1 text-xs font-medium text-secondary-foreground">
        <Sparkles className="size-3.5 text-primary" />
        <span>Rituales e ingredientes botánicos</span>
      </div>

      <h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl md:text-5xl">
        Colección botánica
      </h1>

      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
        Fórmulas botánicas y extractos de hongos funcionales para tu ritual diario de cuidado facial, corporal e interior.
      </p>

      {/* Category Pills Navigation */}
      {loading ? (
        <div className="mt-8 flex flex-wrap justify-center gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              data-index={i}
              className="h-9 w-24 animate-pulse rounded-full bg-muted"
            />
          ))}
        </div>
      ) : (
        terms &&
        terms.length > 0 && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-full border-border bg-card text-card-foreground shadow-2xs hover:bg-accent hover:text-accent-foreground"
              asChild
            >
              <Link to="/ShopPage">
                <Layers className="size-3.5 mr-1.5" />
                Todos los productos
              </Link>
            </Button>
            {terms.map((term, i) => (
              <div key={term.id} data-index={i}>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full border-border bg-card text-card-foreground shadow-2xs hover:bg-accent hover:text-accent-foreground"
                  asChild
                >
                  <Link to={term.link || "/ShopPage"}>
                    {term.name}
                    {typeof term.count === "number" && (
                      <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                        {term.count}
                      </span>
                    )}
                  </Link>
                </Button>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

export default function ArchiveTitle() {
  const current_wp_term_query_params =
    typeof window !== "undefined" && window.wvcClient?.getCurrentWPTermQueryParams
      ? window.wvcClient.getCurrentWPTermQueryParams()
      : { taxonomy: "product_cat", per_page: 8 };

  const wp_term_query = new WP_Term_Query(current_wp_term_query_params);

  return (
    <section
      id="archive-title"
      data-nav="light"
      className="relative w-full border-b border-border bg-background py-10 sm:py-14"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs Row */}
        <div className="mb-6 flex items-center justify-center sm:justify-start">
          <ShopBreadcrumbsProvider>
            <DynamicBreadcrumbsConsumer />
          </ShopBreadcrumbsProvider>
        </div>

        {/* Dynamic Archive Header & Categories */}
        <EcommerceTermsProvider wp_term_query={wp_term_query}>
          <ArchiveHeaderContentConsumer />
        </EcommerceTermsProvider>
      </div>
    </section>
  );
}